import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from "./components/dashboard/layout";

const StudentIdCardDefaulters = () => {
  const [fromDate, setFromDate] = useState(() => new Date());
  const [toDate, setToDate] = useState(() => new Date());
  const [grade, setGrade] = useState('');          // e.g. "Grade 10"
  const [search, setSearch] = useState('');        // name or ps_code
  const [minStreak, setMinStreak] = useState(2);   // consecutive days threshold

  const [defaulters, setDefaulters] = useState([]); // API data (details)
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState(0);   // 0 Summary, 1 Details
  const [error, setError] = useState(null);

  const formatDate = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const styles = {
    container: { padding: '2rem', maxWidth: '100%' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
    title: { fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#333' },
    controlsRow: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '1rem' },
    input: { padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px', minWidth: 160 },
    refreshButton: {
      padding: '10px 20px', backgroundColor: '#1976d2', color: 'white',
      border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', minWidth: '120px'
    },
    refreshButtonDisabled: { backgroundColor: '#ccc', cursor: 'not-allowed' },
    lastUpdated: { fontSize: '0.875rem', color: '#666', marginBottom: '0.75rem' },
    tabs: { display: 'flex', gap: 8, marginTop: '8px', borderBottom: '2px solid #e0e0e0' },
    tab: { padding: '10px 18px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 16 },
    activeTab: { borderBottom: '2px solid #1976d2', color: '#1976d2', fontWeight: 600 },
    cardsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', margin: '12px 0 8px' },
    card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
    cardTitle: { fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
    cardValue: { fontSize: 22, fontWeight: 700, color: '#333' },
    tableWrap: { overflowX: 'auto', marginTop: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' },
    th: { padding: '12px 16px', borderBottom: '2px solid #ddd', fontWeight: 600, color: '#333', background: '#f8f9fa', whiteSpace: 'nowrap', textAlign: 'left' },
    td: { padding: '10px 16px', borderBottom: '1px solid #eee', color: '#444', whiteSpace: 'nowrap' },
    rowEven: { background: '#fafafa' },
    rowOdd: { background: '#fff' },
    empty: { textAlign: 'center', padding: 20, color: '#777' },
    error: { background: '#fff3f3', color: '#b00020', padding: '10px 12px', border: '1px solid #ffcdd2', borderRadius: 8, marginBottom: 8 },
    badge: (bg, color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 12, background: bg, color }),
    exportBtn: { padding: '8px 14px', background: '#455a64', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }
  };

  const fetchDefaulters = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const from = formatDate(fromDate);
      const to = formatDate(toDate);
      const params = new URLSearchParams({
        from_date: from,
        to_date: to,
        grade: grade || '',
        search: search || '',
        min_streak: String(minStreak || 2)
      });
      const url = `https://raptornet.aischennai.org/screening-manager/gate-api/getIDCardDefaulters.php?${params.toString()}`;
      const res = await fetch(url);
      const json = await res.json();

      if (json && json.success && Array.isArray(json.data)) {
        setDefaulters(json.data);
      } else if (Array.isArray(json)) {
        // If API returns raw array
        setDefaulters(json);
      } else {
        setDefaulters([]);
        setError(json?.error || 'No data returned.');
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Defaulters fetch error:', e);
      setError('Failed to load data. Please try again.');
      setDefaulters([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaulters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side summary by grade
  const summaryByGrade = useMemo(() => {
    const map = new Map();
    defaulters.forEach(row => {
      const g = row.current_grade || 'Unknown';
      if (!map.has(g)) map.set(g, { grade: g, students: 0, totalManuals: 0 });
      const bucket = map.get(g);
      bucket.students += 1;
      bucket.totalManuals += Number(row.manual_count || 0);
    });
    return Array.from(map.values()).sort((a, b) => a.grade.localeCompare(b.grade));
  }, [defaulters]);

  const totalStudents = defaulters.length;
  const totalManuals = defaulters.reduce((acc, r) => acc + Number(r.manual_count || 0), 0);

  const toCSV = () => {
    const header = ['PS Code','First Name','Last Name','Grade','Manual Count','Last Manual Date'];
    const rows = defaulters.map(r => [
      r.student_id || '',
      r.first_name || '',
      r.last_name || '',
      r.current_grade || '',
      r.manual_count ?? 0,
      r.last_manual_time || ''
    ]);
    const csv = [header, ...rows].map(cols => cols.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `id-card-defaulters_${formatDate(fromDate)}_to_${formatDate(toDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>ID Card Defaulters (Manual Check-ins)</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...styles.refreshButton, ...(isLoading ? styles.refreshButtonDisabled : {}) }}
              onClick={fetchDefaulters}
              disabled={isLoading}
            >
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button style={styles.exportBtn} onClick={toCSV}>Export CSV</button>
          </div>
        </div>

        {lastUpdated && <div style={styles.lastUpdated}>Last updated: {lastUpdated.toLocaleTimeString()}</div>}
        {error && <div style={styles.error}>{error}</div>}

        {/* Filters */}
        <div style={styles.controlsRow}>
          <input
            type="date"
            value={formatDate(fromDate)}
            onChange={(e) => setFromDate(new Date(e.target.value))}
            style={styles.input}
            aria-label="From date"
          />
          <input
            type="date"
            value={formatDate(toDate)}
            onChange={(e) => setToDate(new Date(e.target.value))}
            style={styles.input}
            aria-label="To date"
          />
          <input
            type="text"
            placeholder="Grade (e.g., Grade 11)"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            style={styles.input}
            aria-label="Grade"
          />
          <input
            type="text"
            placeholder="Search by PS code / Name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...styles.input, minWidth: 220 }}
            aria-label="Search"
          />
          <select
            value={minStreak}
            onChange={(e) => setMinStreak(Number(e.target.value))}
            style={styles.input}
            aria-label="Min consecutive days"
          >
            <option value={2}>Min streak: 2 days</option>
            <option value={3}>Min streak: 3 days</option>
            <option value={4}>Min streak: 4 days</option>
            <option value={5}>Min streak: 5 days</option>
          </select>
          <button
            style={{ ...styles.refreshButton, ...(isLoading ? styles.refreshButtonDisabled : {}) }}
            onClick={fetchDefaulters}
            disabled={isLoading}
          >
            Apply
          </button>
        </div>

        {/* Quick KPI cards */}
        <div style={styles.cardsRow}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Defaulter Students</div>
            <div style={styles.cardValue}>{totalStudents}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Total Manual Check-ins</div>
            <div style={styles.cardValue}>{totalManuals}</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Min Streak</div>
            <div style={styles.cardValue}><span style={styles.badge('#e3f2fd', '#1565c0')}>{minStreak}+ days</span></div>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 0 ? styles.activeTab : {}) }}
            onClick={() => setActiveTab(0)}
          >
            Summary by Grade
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 1 ? styles.activeTab : {}) }}
            onClick={() => setActiveTab(1)}
          >
            Student Details
          </button>
        </div>

        {activeTab === 0 && (
          <>
            {summaryByGrade.length === 0 ? (
              <div style={styles.empty}>{isLoading ? 'Loading summary…' : 'No data for selected filters.'}</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Grade</th>
                      <th style={styles.th}>Defaulter Students</th>
                      <th style={styles.th}>Manual Check-ins</th>
                      <th style={styles.th}>Avg per Student</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByGrade.map((g, idx) => (
                      <tr key={g.grade} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{g.grade}</td>
                        <td style={styles.td}>{g.students}</td>
                        <td style={styles.td}>{g.totalManuals}</td>
                        <td style={styles.td}>{g.students ? (g.totalManuals / g.students).toFixed(2) : '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 1 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>PS Code</th>
                  <th style={styles.th}>First Name</th>
                  <th style={styles.th}>Last Name</th>
                  <th style={styles.th}>Grade</th>
                  <th style={styles.th}>Manual Count</th>
                  <th style={styles.th}>Last Manual Date</th>
                </tr>
              </thead>
              <tbody>
                {defaulters.length === 0 ? (
                  <tr><td colSpan={6} style={styles.empty}>{isLoading ? 'Loading…' : 'No students match these filters.'}</td></tr>
                ) : (
                  defaulters.map((r, idx) => (
                    <tr key={r.student_id + '_' + idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td style={{ ...styles.td, fontWeight: 600 }}>{r.student_id || '-'}</td>
                      <td style={styles.td}>{r.first_name || '-'}</td>
                      <td style={styles.td}>{r.last_name || '-'}</td>
                      <td style={styles.td}>{r.current_grade || '-'}</td>
                      <td style={{ ...styles.td, color: '#c62828', fontWeight: 700 }}>{r.manual_count ?? 0}</td>
                      <td style={styles.td}>{r.last_manual_time || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentIdCardDefaulters;
